import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import HomePage from "@/pages/home";
import PlatformPage from "@/pages/platform";
import PlatformAiPage from "@/pages/platform-ai";
import SolutionsOverviewPage from "@/pages/solutions-overview";
import SolutionsPage from "@/pages/solutions";
import PricingPage from "@/pages/pricing";
import PricingConstructionAddonsPage from "@/pages/pricing-construction-addons";
import PricingVerticalPage from "@/pages/pricing-vertical";
import WordAddinPage from "@/pages/word-addin";
import BlogIndexPage from "@/pages/blog-index";
import BlogPostPage from "@/pages/blog-detail";
import DocsPage from "@/pages/docs";
import WordAddinInstallPage from "@/pages/word-addin-install";
import SalesforceAdminGuidePage from "@/pages/salesforce-admin-guide";
import WebhooksDocsPage from "@/pages/webhooks-docs";
import WebinarsPage from "@/pages/webinars";
import AboutPage from "@/pages/about";
import CareersPage from "@/pages/careers";
import ContactPage from "@/pages/contact";
import DemoPage from "@/pages/demo";
import LegalPage from "@/pages/legal";
import TrustPage from "@/pages/trust";
import NotFound from "@/pages/not-found";
import { usePageViews } from "@/hooks/use-page-views";
import { Sentry } from "@/lib/sentry";

const queryClient = new QueryClient();

function ErrorFallback() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center"
      data-testid="marketing-error-boundary"
    >
      <h1 className="text-lg font-semibold">Something went wrong.</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        An unexpected error has been reported. Please refresh the page to
        continue.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
      >
        Reload
      </button>
    </div>
  );
}

function Router() {
  usePageViews();
  return <Routes />;
}

function Routes() {
  return (
    <Switch>
      <Route path="/" component={HomePage} />

      {/*
        Register `/platform/ai` BEFORE the generic `/platform/:slug`
        catch-all so wouter's <Switch> picks up the dedicated AI
        positioning page instead of falling into PlatformPage with
        slug="ai" (which has no entry in PLATFORM_PAGES and would
        render the "Platform Feature Not Found" branch).
      */}
      <Route path="/platform/ai" component={PlatformAiPage} />
      <Route path="/platform/:slug" component={PlatformPage} />
      <Route path="/solutions" component={SolutionsOverviewPage} />
      <Route path="/solutions/:slug" component={SolutionsPage} />
      <Route path="/pricing" component={PricingPage} />
      {/* Construction add-ons ROI page must be registered BEFORE the
          generic `/pricing/:vertical` route so wouter's <Switch> picks
          the static path first even though `:vertical` only matches a
          single segment today. Cheap insurance against a future router
          change loosening that constraint. */}
      <Route
        path="/pricing/construction/add-ons"
        component={PricingConstructionAddonsPage}
      />
      <Route path="/pricing/:vertical" component={PricingVerticalPage} />
      <Route path="/word-addin" component={WordAddinPage} />
      
      <Route path="/resources/blog" component={BlogIndexPage} />
      <Route path="/resources/blog/:slug" component={BlogPostPage} />
      <Route path="/resources/docs" component={DocsPage} />
      <Route path="/resources/docs/word-addin-install" component={WordAddinInstallPage} />
      <Route path="/resources/docs/salesforce-admin-guide" component={SalesforceAdminGuidePage} />
      <Route path="/resources/docs/webhooks" component={WebhooksDocsPage} />
      <Route path="/resources/webinars" component={WebinarsPage} />
      
      <Route path="/company/about" component={AboutPage} />
      <Route path="/company/careers" component={CareersPage} />
      <Route path="/company/contact" component={ContactPage} />
      
      <Route path="/demo" component={DemoPage} />
      
      <Route path="/legal/:slug" component={LegalPage} />

      <Route path="/trust" component={TrustPage} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <Router />
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
